import React, { useState } from 'react';
import { post } from 'aws-amplify/api';
import ShineEffect from './ShineEffect';

// Define the expected response structure
interface SubscribeResponse {
  duplicate?: boolean;
  message?: string;
  email?: string;
}

const EmailSignup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Cast the response to SubscribeResponse to access our custom properties
      const response = (await post({
        apiName: 'DigitalMemorials',
        path: '/subscribe',
        options: {
          headers: { 'Content-Type': 'application/json' },
          body: { email }
        }
      })) as SubscribeResponse;

      if (response.duplicate) {
        setError(response.message || 'This email has already been registered.');
      } else {
        setSuccess(true);
        setEmail('');
      }
    } catch (err: any) {
      console.error('API Call Error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative w-full overflow-hidden rounded-2xl bg-gradient-to-br from-primary via-[#E6C45C] to-primary group">
      <ShineEffect />
      
      <div className="relative px-4 py-12 sm:px-8 md:px-12">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col items-center md:flex-row md:justify-between md:gap-8">
            {/* Text Content */}
            <div className="mb-6 md:mb-0 text-center md:text-left">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-[#0A0A0A] font-montserrat">
                Register Your Interest
              </h2>
              
              {/* Status Messages */}
              {error && (
                <div className="mt-4 p-3 bg-[#0A0A0A]/10 border border-[#0A0A0A]/20 rounded-xl backdrop-blur-sm">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-[#0A0A0A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-[#0A0A0A] font-montserrat text-sm font-medium">
                      {error}
                    </p>
                  </div>
                </div>
              )}
              {success && (
                <div className="mt-4 p-3 bg-[#0A0A0A]/10 border border-[#0A0A0A]/20 rounded-xl backdrop-blur-sm">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-[#0A0A0A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-[#0A0A0A] font-montserrat text-sm font-medium">
                      Thank you for registering your interest.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Email Form */}
            <div className="w-full max-w-md">
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  disabled={loading}
                  className="flex-1 rounded-xl bg-[#0A0A0A]/10 px-4 py-3 text-[#0A0A0A] placeholder-[#0A0A0A]/60 backdrop-blur-sm border border-[#0A0A0A]/20 focus:border-[#0A0A0A]/30 focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]/30 font-montserrat disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="relative overflow-hidden group rounded-xl bg-[#0A0A0A] px-6 py-3 font-semibold text-primary transition-all duration-200 whitespace-nowrap font-montserrat disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                >
                  <ShineEffect />
                  <span className="relative z-10">
                    {loading ? 'SUBMITTING...' : 'SUBMIT'}
                  </span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailSignup;